/**
 * A batch of art the forge would make costs more than the owner's threshold
 * (ADR-037 D5), so the turn waits here: "Make them" lets the art tool make
 * and write them in the same turn; "Don't make them" declines, and Claude
 * asks what to change. Nothing is made — or charged — before the owner says.
 */
import { useState } from 'react';
import { Loader2, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCredits } from '@/lib/format-credits';
import type { ForgePendingArtBatch } from '../types';

export interface ForgeArtApprovalCardProps {
  pending: ForgePendingArtBatch;
  onDecide: (approved: boolean) => Promise<void>;
}

export function ForgeArtApprovalCard({ pending, onDecide }: ForgeArtApprovalCardProps) {
  const [sending, setSending] = useState<'approve' | 'decline' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { batch } = pending;
  const count = batch.items.length;

  const decide = async (approved: boolean) => {
    if (sending) return;
    setSending(approved ? 'approve' : 'decline');
    setError(null);
    try {
      await onDecide(approved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your decision');
      setSending(null);
    }
  };

  return (
    <section
      aria-label="Art waiting for your approval"
      data-testid="forge-art-approval"
      className="flex max-h-[60vh] flex-col border-t bg-primary/5 px-4 py-3"
    >
      <p className="mb-1 flex items-center gap-2 text-sm font-medium">
        <Palette className="h-4 w-4 text-primary" />
        Claude Code wants to make {count} picture{count === 1 ? '' : 's'} — about{' '}
        {formatCredits(batch.estimateCredits)} credits.
      </p>
      <p className="mb-2 text-xs text-muted-foreground">
        {batch.thresholdCredits === null
          ? 'You asked to approve every batch of art.'
          : `You asked to approve art over ${formatCredits(batch.thresholdCredits)} credits.`}{' '}
        You pay for each picture as it is made.
      </p>
      <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto rounded-md border bg-background px-3 py-2 text-sm">
        {batch.items.map((item) => (
          <li key={item.name} className="flex min-w-0 items-baseline gap-2">
            <span className="shrink-0 font-medium">{item.name}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {item.kind} · {item.style}
            </span>
            {item.description && (
              <span className="truncate text-xs text-muted-foreground" title={item.description}>
                {item.description}
              </span>
            )}
          </li>
        ))}
      </ul>
      {error && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      )}
      <div className="mt-3 flex justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => void decide(false)}
          disabled={sending !== null}
        >
          {sending === 'decline' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Don&apos;t make them
        </Button>
        <Button size="sm" onClick={() => void decide(true)} disabled={sending !== null}>
          {sending === 'approve' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Make them
        </Button>
      </div>
    </section>
  );
}
